import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Heart, MessageCircle, Shield, Users, BookOpen, Search,
  Play, ArrowRight, FileText, Calendar, BarChart3, Lock
} from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunityPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/community`).then(r => setHub(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (authLoading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#4FB3D4] border-t-transparent rounded-full" /></div>;

  if (!user || !user.name) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Navigation />
        <div className="pt-28 pb-20 px-6 flex items-center justify-center">
          <Card className="w-full max-w-md rounded-2xl shadow-floating border-0 text-center" data-testid="community-login-prompt">
            <CardContent className="p-10">
              <Lock className="h-10 w-10 text-[#A69BE0] mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-bold text-[#1A2A3A] mb-3">Community Hub</h1>
              <p className="text-sm text-[#1A2A3A]/60 mb-6">Sign in or create an account to access the ThryvLife Community Hub. It is free to join.</p>
              <div className="flex gap-3 justify-center">
                <Link to="/login"><Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-semibold" data-testid="community-login-btn">Sign In</Button></Link>
                <Link to="/register"><Button variant="outline" className="rounded-full px-6 py-4 border-2 border-[#4FB3D4]/20" data-testid="community-register-btn">Create Account</Button></Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const iconMap = {
    "/chat": MessageCircle, "/pattern-detector": Search, "/share-story": Users,
    "/street-stories": Heart, "/data": BarChart3, "/resources": Shield,
    "/bonus": FileText, "/calendar": Calendar,
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="community-section">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-2">Welcome back, {user.name}</p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="community-title">Community Hub</h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">Your home base. Everything ThryvLife has to offer, all in one place.</p>
          </div>

          {/* Stats */}
          {hub && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                { label: "Community Stories", value: hub.stats.community_stories, color: "text-[#FFB7B2]" },
                { label: "Chat Sessions", value: hub.stats.chat_sessions, color: "text-[#4FB3D4]" },
                { label: "Resources", value: hub.stats.resources_available, color: "text-[#A69BE0]" },
                { label: "PDF Guides", value: hub.stats.pdf_guides, color: "text-[#4FB3D4]" },
              ].map((s, i) => (
                <Card key={i} className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50 p-4 text-center" data-testid={`stat-${i}`}>
                  <p className={`font-heading text-3xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-[#1A2A3A]/50 mt-1">{s.label}</p>
                </Card>
              ))}
            </div>
          )}

          {/* Resources Grid */}
          <h2 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-5">Your Resources</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {hub?.featured_resources?.map((r, i) => {
              const Icon = iconMap[r.link] || Heart;
              return (
                <Link key={i} to={r.link}>
                  <Card className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50 p-4 hover:shadow-card transition-all hover:-translate-y-0.5" data-testid={`resource-${i}`}>
                    <CardContent className="p-0 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#4FB3D4]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-[#4FB3D4]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#1A2A3A] text-sm">{r.title}</h3>
                          {r.free && <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">FREE</span>}
                        </div>
                        <p className="text-xs text-[#1A2A3A]/50 mt-0.5">{r.description}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-[#1A2A3A]/30 flex-shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Video Section */}
          <h2 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-2">Recovery Videos</h2>
          <p className="text-xs text-[#1A2A3A]/50 mb-5">Curated educational content from trusted experts. Videos hosted on YouTube.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {hub?.videos?.map((v, i) => (
              <Card key={i} className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50 overflow-hidden group" data-testid={`video-${i}`}>
                <a href={`https://www.youtube.com/watch?v=${v.youtube_id}`} target="_blank" rel="noopener noreferrer">
                  <div className="relative h-36 bg-gradient-to-br from-[#1A2A3A] to-[#2D4356] flex items-center justify-center">
                    <img src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`} alt={v.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
                    <div className="relative w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="h-5 w-5 text-[#4FB3D4] ml-0.5" />
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-xs text-[#A69BE0] font-medium">{v.source} | {v.duration}</p>
                    <h3 className="font-semibold text-[#1A2A3A] text-sm mt-1 line-clamp-2">{v.title}</h3>
                  </CardContent>
                </a>
              </Card>
            ))}
          </div>

          {/* Monthly Lessons Coming Soon */}
          <Card className="bg-gradient-to-br from-[#1A2A3A] to-[#2D4356] rounded-2xl border-0 p-8 text-center mb-10" data-testid="monthly-lessons-coming-soon">
            <Play className="h-10 w-10 text-[#4FB3D4] mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-white mb-2">Monthly Video Lessons — Coming Soon</h3>
            <p className="text-sm text-white/60 max-w-md mx-auto mb-4">
              Brian Bass is creating AI-powered monthly video lessons and "Stories from the Streets" documentary profiles. Premium members get first access.
            </p>
            <Link to="/checkout?plan=premium">
              <Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-semibold text-sm">
                Get Premium Access <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Card>

          {/* Upgrade CTAs */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-[#A69BE0]/10 to-[#A69BE0]/5 rounded-xl border border-[#A69BE0]/20 p-6" data-testid="lifetime-growth">
              <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-2">Growth Bundle — Lifetime</h3>
              <p className="text-sm text-[#1A2A3A]/60 mb-3">One-time payment. Everything in the Growth tier forever.</p>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-heading text-3xl font-bold text-[#A69BE0]">$85</span>
                <span className="text-sm text-[#1A2A3A]/40 line-through">$47/mo</span>
                <span className="text-xs text-green-600 font-semibold">Save after 2 months</span>
              </div>
              <Link to="/checkout?plan=growth"><Button className="bg-[#A69BE0] hover:bg-[#A69BE0]/90 text-white rounded-full px-5 py-3 text-sm font-semibold w-full">Get Lifetime Growth</Button></Link>
            </Card>
            <Card className="bg-gradient-to-br from-[#FFB7B2]/10 to-[#FFB7B2]/5 rounded-xl border border-[#FFB7B2]/20 p-6" data-testid="lifetime-premium">
              <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-2">Premium — Lifetime</h3>
              <p className="text-sm text-[#1A2A3A]/60 mb-3">One-time payment. The complete arsenal forever.</p>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-heading text-3xl font-bold text-[#1A2A3A]">$100</span>
                <span className="text-sm text-[#1A2A3A]/40 line-through">$67/mo</span>
                <span className="text-xs text-green-600 font-semibold">Save after 2 months</span>
              </div>
              <Link to="/checkout?plan=premium"><Button className="bg-[#1A2A3A] hover:bg-[#1A2A3A]/90 text-white rounded-full px-5 py-3 text-sm font-semibold w-full">Get Lifetime Premium</Button></Link>
            </Card>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 bg-[#1A2A3A] text-white text-center">
        <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB). Incremental Steps. Exponential Thryving.</p>
      </footer>
    </div>
  );
};

export default CommunityPage;
